import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Target, AlertTriangle, ArrowRightLeft, Flame, Heart, HandMetal, Frown, Zap, Tv, Bell, BellOff, Info, Shield, CheckCircle, UserPlus, UserMinus, Database } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { getTeamFlag } from '../utils/teamFlags';
import { AlertOptIn } from '../components/AlertComponents';

const API = process.env.REACT_APP_BACKEND_URL;

const REACTION_MAP = {
  fire: { icon: Flame, label: 'Fire', color: 'text-orange-500' },
  goal: { icon: Target, label: 'Goal!', color: 'text-emerald-500' },
  clap: { icon: HandMetal, label: 'Clap', color: 'text-yellow-500' },
  love: { icon: Heart, label: 'Love', color: 'text-red-500' },
  sad: { icon: Frown, label: 'Sad', color: 'text-blue-400' },
  shocked: { icon: Zap, label: 'Wow', color: 'text-purple-400' },
};

const EVENT_ICONS = {
  goal: () => <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-black shadow-lg shadow-emerald-500/30">G</div>,
  yellow_card: () => <div className="w-5 h-7 rounded-[2px] bg-yellow-400 border border-yellow-500" />,
  red_card: () => <div className="w-7 h-9 rounded-[2px] bg-red-500 border border-red-600 shadow-lg shadow-red-500/30" />,
  penalty: () => <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black text-[10px] font-black">PK</div>,
  substitution: () => <ArrowRightLeft className="w-5 h-5 text-sky-400" />,
  chance: () => <Target className="w-5 h-5 text-gray-400" />,
  kickoff: () => <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px]">KO</div>,
  halftime: () => <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px]">HT</div>,
  fulltime: () => <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px]">FT</div>,
  whistle: () => <Clock className="w-5 h-5 text-white/40" />,
  foul: () => <AlertTriangle className="w-5 h-5 text-yellow-600" />,
  corner: () => <div className="w-5 h-5 rounded-sm bg-white/10 flex items-center justify-center text-[9px] text-white/50">CK</div>,
};

function StatBar({ label, home, away, unit = '' }) {
  const total = (home || 0) + (away || 0);
  const homeP = total > 0 ? (home / total) * 100 : 50;
  return (
    <div className="py-2" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-white font-semibold tabular-nums">{home}{unit}</span>
        <span className="text-gray-500 text-xs uppercase tracking-wider">{label}</span>
        <span className="text-white font-semibold tabular-nums">{away}{unit}</span>
      </div>
      <div className="flex gap-1 h-1.5 rounded-full overflow-hidden bg-white/5">
        <div className="bg-emerald-500 rounded-l-full transition-all duration-700" style={{ width: `${homeP}%` }} />
        <div className="bg-white/20 rounded-r-full transition-all duration-700" style={{ width: `${100 - homeP}%` }} />
      </div>
    </div>
  );
}

export default function MatchDay() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('events');
  const [reactionCounts, setReactionCounts] = useState({});
  const [lastReaction, setLastReaction] = useState(null);
  const [reminded, setReminded] = useState(false);
  const [followingHome, setFollowingHome] = useState(false);
  const [followingAway, setFollowingAway] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showAlertOptIn, setShowAlertOptIn] = useState(false);
  const [alertOptInTeam, setAlertOptInTeam] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/matchday/${matchId}`);
      const json = await res.json();
      if (json.error) return;
      setData(json);
      setReactionCounts(json.reactions || {});
      // Track match view for retention metrics
      const token = localStorage.getItem('token');
      if (token) {
        fetch(`${API}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ event_type: 'match_view', match_id: matchId }),
        }).catch(() => {});
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [matchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check follow status
  useEffect(() => {
    if (!data?.match) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API}/api/user/following`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(json => {
        const teams = json.teams || [];
        setFollowingHome(teams.some(t => t.toLowerCase() === data.match.home.toLowerCase()));
        setFollowingAway(teams.some(t => t.toLowerCase() === data.match.away.toLowerCase()));
      })
      .catch(() => {});
  }, [data?.match]);

  useEffect(() => {
    const reminders = JSON.parse(localStorage.getItem('afrosports_reminders') || '[]');
    setReminded(reminders.includes(matchId));
  }, [matchId]);

  useEffect(() => {
    if (!data?.clock?.status || data.clock.status !== 'live') return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [data?.clock?.status, fetchData]);

  const sendReaction = async (type) => {
    setLastReaction(type);
    setReactionCounts(prev => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
    setTimeout(() => setLastReaction(null), 600);
    try {
      const res = await fetch(`${API}/api/matchday/${matchId}/react`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const json = await res.json();
      if (json.counts) setReactionCounts(json.counts);
    } catch { /* silent */ }
  };

  const toggleReminder = () => {
    const reminders = JSON.parse(localStorage.getItem('afrosports_reminders') || '[]');
    if (reminded) {
      localStorage.setItem('afrosports_reminders', JSON.stringify(reminders.filter(r => r !== matchId)));
      setReminded(false);
    } else {
      reminders.push(matchId);
      localStorage.setItem('afrosports_reminders', JSON.stringify(reminders));
      setReminded(true);
    }
  };

  const toggleFollow = async (teamName, isFollowing, setIsFollowing) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setFollowLoading(true);
    try {
      const endpoint = isFollowing ? 'unfollow-team' : 'follow-team';
      await fetch(`${API}/api/user/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ team_name: teamName }),
      });
      setIsFollowing(!isFollowing);
      // Show alert opt-in after following (not unfollowing)
      if (!isFollowing && 'Notification' in window && Notification.permission !== 'granted') {
        const dismissed = sessionStorage.getItem('alert_optin_dismissed');
        if (!dismissed) {
          setAlertOptInTeam(teamName);
          setShowAlertOptIn(true);
        }
      }
    } catch { /* silent */ }
    finally { setFollowLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh] text-gray-400">
          <p className="text-xl font-semibold mb-2">Match not found</p>
          <Link to="/" className="text-emerald-500 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const { match, score, clock, events, key_moments, stats } = data;
  const homeFlag = getTeamFlag(match.home);
  const awayFlag = getTeamFlag(match.away);
  const isLive = clock.status === 'live';
  const isUpcoming = clock.status === 'upcoming';
  const isSimulated = data.data_source === 'simulated';
  const isReal = data.data_source === 'thesportsdb';

  const tabs = [
    { id: 'events', label: 'Events' },
    { id: 'stats', label: 'Stats' },
    { id: 'key_moments', label: 'Key Moments' },
    { id: 'where_to_watch', label: 'Where to Watch' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="matchday-page">
      <Navbar />

      {/* MATCH HEADER */}
      <div className="bg-gradient-to-b from-[#111] to-[#0a0a0a] border-b border-white/5" data-testid="matchday-header">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-8">
          {/* Back + competition + date */}
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="back-button">
              <ArrowLeft className="w-4 h-4 text-white/60" />
            </Link>
            {match.competition && (
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider">{match.competition}</span>
            )}
            {match.round && (
              <span className="text-gray-500 text-xs">Round {match.round}</span>
            )}
            {isLive && (
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-400 text-xs font-bold uppercase tracking-wider">LIVE &middot; {clock.minute}'</span>
              </div>
            )}
            {isUpcoming && (
              <span className="text-amber-400 text-xs font-medium ml-auto uppercase">Upcoming</span>
            )}
            {!isLive && !isUpcoming && (
              <span className="text-gray-500 text-xs font-medium ml-auto uppercase">Full Time</span>
            )}
          </div>

          {/* Scoreboard */}
          <div className="flex items-center justify-center gap-6 sm:gap-10" data-testid="matchday-scoreboard">
            {/* Home */}
            <div className="flex flex-col items-center gap-2 min-w-[100px]">
              <span className="text-4xl sm:text-5xl">{homeFlag || ''}</span>
              <span className="text-white font-bold text-sm sm:text-base text-center leading-tight">{match.home}</span>
              <FollowButton
                teamName={match.home}
                isFollowing={followingHome}
                onToggle={() => toggleFollow(match.home, followingHome, setFollowingHome)}
                disabled={followLoading}
              />
            </div>
            {/* Score */}
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="text-5xl sm:text-6xl font-black text-white tabular-nums" data-testid="home-score">{score.home}</span>
              <span className="text-white/20 text-2xl font-light">-</span>
              <span className="text-5xl sm:text-6xl font-black text-white tabular-nums" data-testid="away-score">{score.away}</span>
            </div>
            {/* Away */}
            <div className="flex flex-col items-center gap-2 min-w-[100px]">
              <span className="text-4xl sm:text-5xl">{awayFlag || ''}</span>
              <span className="text-white font-bold text-sm sm:text-base text-center leading-tight">{match.away}</span>
              <FollowButton
                teamName={match.away}
                isFollowing={followingAway}
                onToggle={() => toggleFollow(match.away, followingAway, setFollowingAway)}
                disabled={followLoading}
              />
            </div>
          </div>

          {/* Match minute bar */}
          {isLive && (
            <div className="mt-5 max-w-md mx-auto">
              <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000" style={{ width: `${Math.min((clock.minute / 90) * 100, 100)}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>0'</span><span>45'</span><span>90'</span>
              </div>
            </div>
          )}

          {/* Date + venue info */}
          {(match.date || match.venue) && (
            <div className="flex items-center justify-center gap-2 mt-3 text-gray-500 text-xs">
              {match.date && <span>{match.date}</span>}
              {match.date && match.venue && <span>&middot;</span>}
              {match.venue && <span>{match.venue}</span>}
            </div>
          )}

          {/* Remind Me button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={toggleReminder}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                reminded
                  ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
                  : 'bg-white/[0.04] border border-white/[0.08] text-white/60 hover:bg-white/[0.08] hover:text-white'
              }`}
              data-testid="remind-me-btn"
            >
              {reminded ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
              {reminded ? 'Reminder Set' : 'Remind Me'}
            </button>
          </div>
        </div>
      </div>

      {/* DATA SOURCE BANNER */}
      {isSimulated && (
        <div className="bg-amber-500/10 border-y border-amber-500/20" data-testid="simulated-data-banner">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-300 text-sm font-semibold">Sample Match Data</p>
              <p className="text-amber-400/70 text-xs">Events, scores, and statistics shown are simulated for demonstration. Live data integration is coming soon.</p>
            </div>
            <Shield className="w-5 h-5 text-amber-500/40 shrink-0 ml-auto" />
          </div>
        </div>
      )}
      {isReal && (
        <div className="bg-emerald-500/8 border-y border-emerald-500/15" data-testid="real-data-banner">
          <div className="max-w-4xl mx-auto px-4 py-2.5 flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-3 h-3 text-emerald-500/60" />
              <p className="text-emerald-400/80 text-xs font-medium">{data.attribution || 'Verified match data'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Upcoming match info */}
      {isUpcoming && (
        <div className="bg-sky-500/8 border-y border-sky-500/15" data-testid="upcoming-match-banner">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sky-500/15 flex items-center justify-center shrink-0">
              <Clock className="w-4 h-4 text-sky-400" />
            </div>
            <div>
              <p className="text-sky-300 text-sm font-semibold">Match Not Yet Started</p>
              <p className="text-sky-400/70 text-xs">This match hasn't kicked off yet. Set a reminder to get notified!</p>
            </div>
          </div>
        </div>
      )}

      {/* FAN REACTIONS */}
      {!isUpcoming && (
        <div className="bg-[#0d0d0d] border-b border-white/5 py-4" data-testid="matchday-reactions">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
              {Object.entries(REACTION_MAP).map(([key, { icon: Icon, label, color }]) => (
                <button
                  key={key}
                  onClick={() => sendReaction(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-200 ${
                    lastReaction === key
                      ? 'border-white/30 bg-white/10 scale-110'
                      : 'border-white/[0.06] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/10'
                  }`}
                  data-testid={`reaction-${key}`}
                >
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-white/70 text-xs font-medium">{reactionCounts[key] || 0}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="max-w-4xl mx-auto px-4 mt-6">
        <div className="flex gap-1 border-b border-white/5 mb-6" data-testid="matchday-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-semibold transition-colors relative ${
                activeTab === tab.id ? 'text-emerald-400' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-t" />
              )}
            </button>
          ))}
        </div>

        {/* EVENTS TAB */}
        {activeTab === 'events' && (
          <div className="space-y-1 pb-12" data-testid="events-timeline">
            {isUpcoming ? (
              <div className="text-center py-16 text-gray-500">
                <Clock className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="text-sm">Match events will appear here once the game kicks off.</p>
              </div>
            ) : events.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No events available.</p>
            ) : (
              (() => {
                // For live matches: sort high-priority events to top (newest first)
                const sorted = [...events].reverse();
                if (isLive) {
                  const highPriority = ['goal', 'red_card', 'penalty'];
                  sorted.sort((a, b) => {
                    const aHigh = highPriority.includes(a.type);
                    const bHigh = highPriority.includes(b.type);
                    if (aHigh && !bHigh) return -1;
                    if (!aHigh && bHigh) return 1;
                    return b.minute - a.minute;
                  });
                }
                // Find latest goal for live highlight
                const latestGoalMin = isLive ? Math.max(0, ...events.filter(e => e.type === 'goal').map(e => e.minute)) : -1;
                return sorted.map((evt, i) => {
                  const IconComp = EVENT_ICONS[evt.icon] || EVENT_ICONS[evt.type];
                  const isGoal = evt.type === 'goal';
                  const isRed = evt.type === 'red_card';
                  const isPenalty = evt.type === 'penalty';
                  const isLatestGoal = isLive && isGoal && evt.minute === latestGoalMin;
                  return (
                    <div key={i} className={`flex items-start gap-3 rounded-xl transition-colors ${
                      isGoal
                        ? `py-3.5 px-4 bg-emerald-500/[0.08] border ${isLatestGoal ? 'border-emerald-500/30 ring-1 ring-emerald-500/20' : 'border-emerald-500/10'}`
                        : isRed
                        ? 'py-3 px-4 bg-red-500/[0.06] border border-red-500/10'
                        : isPenalty
                        ? 'py-3 px-4 bg-amber-500/[0.06] border border-amber-500/10'
                        : 'py-2.5 px-3 hover:bg-white/[0.02]'
                    }`} data-testid={`event-${i}`}>
                      {/* Minute */}
                      <span className={`text-xs font-mono w-8 pt-1 shrink-0 text-right ${
                        isGoal ? 'text-emerald-400 font-bold' : isRed ? 'text-red-400 font-bold' : 'text-gray-500'
                      }`}>{evt.minute}'</span>
                      {/* Icon */}
                      <div className="shrink-0 pt-0.5">{IconComp ? <IconComp /> : null}</div>
                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        {isGoal && (
                          <span className="text-emerald-400 text-[9px] font-black uppercase tracking-widest block mb-0.5">GOAL</span>
                        )}
                        {isRed && (
                          <span className="text-red-400 text-[9px] font-black uppercase tracking-widest block mb-0.5">RED CARD</span>
                        )}
                        {isPenalty && (
                          <span className="text-amber-400 text-[9px] font-black uppercase tracking-widest block mb-0.5">PENALTY</span>
                        )}
                        <p className={`leading-snug ${
                          isGoal ? 'text-white font-bold text-base' : isRed ? 'text-white font-semibold text-sm' : 'text-gray-300 text-sm'
                        }`}>
                          {evt.text}
                        </p>
                        {evt.team && (
                          <span className={`text-[10px] uppercase tracking-wider mt-0.5 block ${
                            isGoal ? 'text-emerald-400/70' : isRed ? 'text-red-400/70' : 'text-gray-500'
                          }`}>
                            {evt.team === 'home' ? match.home : match.away}
                          </span>
                        )}
                      </div>
                      {/* Live: latest goal pulse */}
                      {isLatestGoal && (
                        <span className="shrink-0 w-2 h-2 rounded-full bg-emerald-500 animate-pulse mt-2" />
                      )}
                    </div>
                  );
                });
              })()
            )}
          </div>
        )}

        {/* STATS TAB */}
        {activeTab === 'stats' && (
          <div className="pb-12" data-testid="stats-panel">
            {isUpcoming || !stats ? (
              <div className="text-center py-16 text-gray-500">
                <Target className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="text-sm">Match statistics will be available once the game starts.</p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-500 uppercase tracking-wider mb-4 px-1">
                  <span>{match.home}</span>
                  <span>{match.away}</span>
                </div>
                {stats.possession && <StatBar label="Possession" home={stats.possession.home} away={stats.possession.away} unit="%" />}
                {stats.shots && <StatBar label="Shots" home={stats.shots.home} away={stats.shots.away} />}
                {stats.shots_on_target && <StatBar label="Shots on Target" home={stats.shots_on_target.home} away={stats.shots_on_target.away} />}
                {stats.corners && <StatBar label="Corners" home={stats.corners.home} away={stats.corners.away} />}
                {stats.fouls && <StatBar label="Fouls" home={stats.fouls.home} away={stats.fouls.away} />}
                {stats.offsides && <StatBar label="Offsides" home={stats.offsides.home} away={stats.offsides.away} />}
                {stats.passes && <StatBar label="Passes" home={stats.passes.home} away={stats.passes.away} />}
                {stats.pass_accuracy && <StatBar label="Pass Accuracy" home={stats.pass_accuracy.home} away={stats.pass_accuracy.away} unit="%" />}
              </div>
            )}
          </div>
        )}

        {/* KEY MOMENTS TAB */}
        {activeTab === 'key_moments' && (
          <div className="pb-12" data-testid="key-moments-timeline">
            {isUpcoming ? (
              <div className="text-center py-16 text-gray-500">
                <Zap className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="text-sm">Key moments will appear here during and after the match.</p>
              </div>
            ) : key_moments.length === 0 ? (
              <p className="text-gray-500 text-center py-10">No key moments yet.</p>
            ) : (
              <div className="relative">
                <div className="absolute left-[18px] top-0 bottom-0 w-px bg-white/5" />

                {/* TOP MOMENTS — Full Time summary with clickable pills */}
                {(() => {
                  const topTypes = ['goal', 'red_card', 'penalty'];
                  const topMoments = key_moments.filter(m => topTypes.includes(m.type));
                  if (topMoments.length === 0) return null;
                  return (
                    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-500/[0.06] to-transparent border border-emerald-500/10" data-testid="top-moments-summary">
                      <h4 className="text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
                        {isLive ? 'Key Moments So Far' : 'Top Moments'}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {topMoments.map((m, i) => {
                          const isGoal = m.type === 'goal';
                          const isRed = m.type === 'red_card';
                          return (
                            <button
                              key={i}
                              onClick={() => {
                                const el = document.querySelector(`[data-moment-id="km-${i}"]`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-105 cursor-pointer ${
                                isGoal
                                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/25'
                                  : isRed
                                  ? 'bg-red-500/15 text-red-300 border border-red-500/20 hover:bg-red-500/25'
                                  : 'bg-amber-500/15 text-amber-300 border border-amber-500/20 hover:bg-amber-500/25'
                              }`}
                              data-testid={`top-moment-pill-${i}`}
                            >
                              {isGoal && <Target className="w-3 h-3" />}
                              {isRed && <AlertTriangle className="w-3 h-3" />}
                              {!isGoal && !isRed && <Zap className="w-3 h-3" />}
                              <span>{m.minute}'</span>
                              <span>{m.player || (isGoal ? 'Goal' : isRed ? 'Red Card' : 'Penalty')}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline */}
                <div className="space-y-4">
                  {key_moments.map((evt, i) => {
                    const IconComp = EVENT_ICONS[evt.icon] || EVENT_ICONS[evt.type];
                    const isGoal = evt.type === 'goal';
                    const isRed = evt.type === 'red_card';
                    const isPenalty = evt.type === 'penalty';
                    return (
                      <div
                        key={i}
                        data-moment-id={`km-${i}`}
                        className={`flex items-start gap-4 relative rounded-xl px-3 py-2 transition-colors ${
                          isGoal ? 'bg-emerald-500/[0.04]' : isRed ? 'bg-red-500/[0.04]' : ''
                        }`}
                        data-testid={`key-moment-${i}`}
                      >
                        <div className="relative z-10 shrink-0">
                          {IconComp ? <IconComp /> : <div className="w-6 h-6 rounded-full bg-white/10" />}
                        </div>
                        <div className="pt-0.5 flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold ${
                              isGoal ? 'text-emerald-400' : isRed ? 'text-red-400' : isPenalty ? 'text-amber-400' : 'text-emerald-400'
                            }`}>{evt.minute}'</span>
                            <span className={`font-semibold text-sm ${
                              isGoal ? 'text-emerald-300' : isRed ? 'text-red-300' : 'text-white'
                            }`}>
                              {isGoal ? 'GOAL!' : isRed ? 'RED CARD' : evt.type === 'halftime' ? 'HALF TIME' : evt.type.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <p className={`text-sm mt-0.5 ${isGoal || isRed ? 'text-gray-300' : 'text-gray-400'}`}>{evt.text}</p>
                          {evt.team && (
                            <span className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5 block">
                              {evt.team === 'home' ? match.home : match.away}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* WHERE TO WATCH TAB */}
        {activeTab === 'where_to_watch' && (
          <div className="pb-12" data-testid="where-to-watch-panel">
            <p className="text-gray-400 text-sm mb-6">Find this match on these broadcasters across Africa.</p>
            {(data.where_to_watch || []).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.where_to_watch.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-emerald-500/20 transition-colors" data-testid={`broadcaster-${i}`}>
                    <Tv className="w-5 h-5 text-emerald-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm">{b.name}</p>
                      <p className="text-gray-500 text-xs">{b.regions}</p>
                    </div>
                    {b.type === 'free' && <span className="ml-auto text-emerald-400 text-[9px] font-bold uppercase tracking-wider bg-emerald-500/10 px-2 py-0.5 rounded-full">Free</span>}
                    {b.type === 'subscription' && <span className="ml-auto text-amber-400 text-[9px] font-bold uppercase tracking-wider bg-amber-500/10 px-2 py-0.5 rounded-full">Sub</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 rounded-xl bg-white/[0.02] border border-white/5">
                <Tv className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Broadcaster information not yet available for this match.</p>
                <p className="text-gray-600 text-xs mt-1">Check back closer to kickoff.</p>
              </div>
            )}

            {/* Pre-match preview section */}
            {data.preview && (
              <div className="mt-8" data-testid="prematch-preview">
                <h3 className="text-white font-bold text-sm mb-4 uppercase tracking-wider">Pre-Match Preview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {data.preview.head_to_head && (
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">Head to Head</p>
                      <div className="space-y-1.5">
                        <div className="flex justify-between"><span className="text-white/70 text-xs">{match.home} wins</span><span className="text-white font-bold text-sm">{data.preview.head_to_head.home_wins}</span></div>
                        <div className="flex justify-between"><span className="text-white/70 text-xs">Draws</span><span className="text-white font-bold text-sm">{data.preview.head_to_head.draws}</span></div>
                        <div className="flex justify-between"><span className="text-white/70 text-xs">{match.away} wins</span><span className="text-white font-bold text-sm">{data.preview.head_to_head.away_wins}</span></div>
                      </div>
                    </div>
                  )}
                  {data.preview.home_form && (
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">{match.home} Form</p>
                      <div className="flex gap-1">
                        {data.preview.home_form.map((r, i) => (
                          <span key={i} className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold ${r === 'W' ? 'bg-emerald-500/20 text-emerald-400' : r === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/50'}`}>{r}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.preview.away_form && (
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-2">{match.away} Form</p>
                      <div className="flex gap-1">
                        {data.preview.away_form.map((r, i) => (
                          <span key={i} className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold ${r === 'W' ? 'bg-emerald-500/20 text-emerald-400' : r === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/50'}`}>{r}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Alert Opt-In Prompt */}
      {showAlertOptIn && (
        <AlertOptIn
          teamName={alertOptInTeam}
          onClose={() => {
            setShowAlertOptIn(false);
            sessionStorage.setItem('alert_optin_dismissed', 'true');
          }}
        />
      )}
    </div>
  );
}

function FollowButton({ teamName, isFollowing, onToggle, disabled }) {
  const token = localStorage.getItem('token');
  if (!token) return null;

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all ${
        isFollowing
          ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-400'
          : 'bg-white/[0.04] border border-white/[0.08] text-white/50 hover:bg-white/[0.08] hover:text-white'
      }`}
      data-testid={`follow-${teamName.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isFollowing ? <UserMinus className="w-3 h-3" /> : <UserPlus className="w-3 h-3" />}
      {isFollowing ? 'Following' : 'Follow'}
    </button>
  );
}
